import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, Observable } from 'rxjs';
import { DeviceType, MediaQuerySize } from '@shared/constants';
import { MediaQueryType } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class MediaObserverService {
  private readonly breakpointObserver: BreakpointObserver =
    inject(BreakpointObserver);

  public device: WritableSignal<DeviceType> = signal(DeviceType.MOBILE);
  public mqAlias: WritableSignal<MediaQueryType> = signal(MediaQuerySize.SM);

  /**
   * Whether the device supports touch — phones and tablets, including large
   * tablets in landscape — where a camera scanner is usable, as opposed to a
   * mouse-driven desktop/laptop. Capability-based, so it does NOT depend on
   * screen width. Combines the coarse-pointer / no-hover media queries with
   * `navigator.maxTouchPoints`, which also catches devices that misreport those
   * media features (e.g. iPadOS Safari in "Request Desktop Website" mode).
   */
  public readonly isTouchDevice = toSignal(
    this.breakpointObserver
      .observe(['(pointer: coarse)', '(hover: none)'])
      .pipe(
        map(
          (state) =>
            Object.values(state.breakpoints).some(Boolean) ||
            this.hasTouchPoints
        )
      ),
    { initialValue: false }
  );

  /** Touch-capable hardware, guarded for non-browser (SSR) environments. */
  private get hasTouchPoints(): boolean {
    return typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
  }

  /**
   * Whether the device runs Android — phones and tablets alike. User-agent
   * based because no media query exposes the OS. Used to opt a retailer out of
   * the camera scanner where the partner's Android webview cannot serve it.
   * Guarded for non-browser (SSR) environments, where it resolves to `false`.
   */
  public isAndroid(): boolean {
    return (
      typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)
    );
  }

  /**
   * Observe Media query changes
   * @returns Observable<BreakpointState>
   */
  observe(): Observable<BreakpointState> {
    return this.breakpointObserver.observe([
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge,
    ]);
  }
}
